import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, LogIn, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input, Label } from '../../components/ui/Form';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const loginSchema = z.object({
  email: z.string()
    .min(1, 'Email is required')
    .email('Invalid email address'),
  password: z.string()
    .min(1, 'Password is required')
    .min(6, 'Password must be at least 6 characters'),
  rememberMe: z.boolean().optional(),
});

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const result = await login(data);
      if (result.success) {
        toast.success('Login successful!', `Welcome back, ${result.user?.full_name}`);
        if (result.user?.force_password_change) {
          navigate('/force-change-password');
        } else {
          navigate('/dashboard');
        }
      } else {
        setLoginAttempts(prev => prev + 1);
        toast.error(result.error, 'Login failed');
        
        if (loginAttempts >= 2) {
          toast.warning('Too many failed attempts', 'Please contact administrator');
        }
      }
    } catch (error) {
      toast.error('Network error', 'Please check your connection');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-orange-500 via-orange-50 to-amber-400 flex items-center justify-center p-4 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10 pointer-events-none z-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-orange-500 rounded-full filter blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-amber-400 rounded-full filter blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md relative z-20 space-y-8"
      >
        {/* Header Card */}
        <Card className="border-0 shadow-strong">
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-amber-400 rounded-lg flex items-center justify-center mx-auto mb-4">
              <LogIn className="text-white" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Staff Portal
            </h1>
            <p className="text-gray-600">
              Food For Life Nepal Management System
            </p>
            <Badge variant="info" className="mt-2">
              Staff Login
            </Badge>
          </CardContent>
        </Card>

        {/* Login Form Card */}
        <Card className="shadow-strong border-0">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl text-center">
              Sign In to Your Account
            </CardTitle>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              {/* Email Field */}
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your staff email"
                      {...field}
                    />
                  )}
                />
                {errors.email && (
                  <div className="flex items-center mt-1 text-red-500 text-sm">
                    <AlertCircle size={14} className="mr-1" />
                    {errors.email.message}
                  </div>
                )}
              </div>

              {/* Password Field */}
              <div>
                <Label htmlFor="password">Password</Label>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  )}
                />
                {errors.password && (
                  <div className="flex items-center mt-1 text-red-500 text-sm">
                    <AlertCircle size={14} className="mr-1" />
                    {errors.password.message}
                  </div>
                )}
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <Controller
                  name="rememberMe"
                  control={control}
                  render={({ field }) => (
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                        checked={field.value}
                        onChange={field.onChange}
                      />
                      <span className="ml-2 text-sm text-gray-600">Remember me</span>
                    </label>
                  )}
                />
                <button
                  type="button"
                  className="text-sm text-orange-500 hover:text-orange-600 font-medium"
                >
                  Forgot password?
                </button>
              </div>

              {/* Login Button */}
              <Button
                type="submit"
                className="w-full"
                loading={loading}
                size="lg"
                disabled={loginAttempts >= 3}
              >
                {loginAttempts >= 3 ? 'Account Locked' : 'Sign In'}
              </Button>

              {/* Login Attempts Warning */}
              {loginAttempts > 0 && (
                <div className="text-center">
                  {loginAttempts < 3 ? (
                    <p className="text-sm text-amber-600">
                      {3 - loginAttempts} attempts remaining
                    </p>
                  ) : (
                    <p className="text-sm text-red-600">
                      Too many failed attempts. Please contact administrator.
                    </p>
                  )}
                </div>
              )}
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Need help?{' '}
                <button
                  type="button"
                  className="text-orange-500 hover:text-orange-600 font-medium"
                >
                  Contact IT Support
                </button>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">
            © 2024 Food For Life Nepal. All rights reserved.
          </p>
          <div className="flex justify-center space-x-4 mt-2">
            <button type="button" className="text-xs text-gray-500 hover:text-gray-700">
              Privacy Policy
            </button>
            <button type="button" className="text-xs text-gray-500 hover:text-gray-700">
              Terms of Service
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
